/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function () {
  function enhanceDillzPrivacyPolicy() {
    if (window.location.pathname !== "/policies/privacy-policy") {
      return;
    }

    var rte = document.querySelector(".shopify-policy__body .rte");
    if (!rte || rte.dataset.dillzPrivacyEnhanced === "true") {
      return;
    }

    rte.dataset.dillzPrivacyEnhanced = "true";

    var root = rte.firstElementChild && rte.firstElementChild.tagName === "DIV" ? rte.firstElementChild : rte;
    root.classList.add("dillz-privacy-root");

    var allNodes = Array.prototype.slice.call(root.children);
    var allHeadings = Array.prototype.slice.call(root.querySelectorAll("h2"));
    var allTables = Array.prototype.slice.call(root.querySelectorAll("table"));

    var pill = allNodes.find(function (node) {
      return /Dillz privacy policy/i.test(node.textContent || "");
    });

    var heroTitle = allHeadings.find(function (node) {
      return /Privacy that keeps things clear/i.test(node.textContent || "");
    });

    if (pill) {
      pill.classList.add("dillz-privacy-pill");
    }

    if (heroTitle) {
      heroTitle.classList.add("dillz-privacy-hero-title");

      if (heroTitle.nextElementSibling && heroTitle.nextElementSibling.tagName === "P") {
        heroTitle.nextElementSibling.classList.add("dillz-privacy-hero-copy");
      }
    }

    var statCards = allNodes.filter(function (node) {
      return node.tagName === "DIV" && /Last updated|Covers|Questions/i.test(node.textContent || "");
    });

    statCards.forEach(function (node) {
      node.classList.add("dillz-privacy-stat-card");
    });

    if (allTables[0]) {
      allTables[0].classList.add("dillz-privacy-stats");
    }

    if (allTables[1]) {
      allTables[1].classList.add("dillz-privacy-data-table");
    }

    allHeadings.forEach(function (heading) {
      var text = heading.textContent || "";

      if (heading === heroTitle) {
        return;
      }

      if (/^Contact$/i.test(text.trim())) {
        heading.classList.add("dillz-privacy-contact-heading");

        if (heading.nextElementSibling && heading.nextElementSibling.tagName === "P") {
          heading.nextElementSibling.classList.add("dillz-privacy-contact-copy");
        }

        return;
      }

      heading.classList.add("dillz-privacy-section-heading");
    });

    var style = document.createElement("style");
    style.id = "dillz-privacy-policy-style";
    style.textContent = [
      ".dillz-privacy-root{max-width:760px;margin:-8px auto 48px;padding:0 18px;color:#111111;}",
      ".dillz-privacy-root p,.dillz-privacy-root li{font-size:15px;line-height:1.7;}",
      ".dillz-privacy-root a{color:#006d3d;font-weight:800;text-decoration:underline;}",
      ".dillz-privacy-pill{display:block!important;width:max-content;max-width:100%;background:#E0E31C!important;color:#102414!important;font-size:12px!important;font-weight:800!important;letter-spacing:.08em!important;text-transform:uppercase!important;border-radius:999px!important;padding:8px 13px!important;margin:-8px auto 16px!important;text-align:center!important;}",
      ".dillz-privacy-hero-title{background:#006D3D!important;color:#ffffff!important;border-radius:28px 28px 0 0!important;padding:30px 26px 8px!important;margin:0!important;text-align:center!important;font-size:32px!important;line-height:1.12!important;}",
      ".dillz-privacy-hero-copy{background:#006D3D!important;color:#f4f8ec!important;border-radius:0 0 28px 28px!important;padding:0 28px 32px!important;margin:0 0 22px!important;text-align:center!important;font-size:16px!important;line-height:1.65!important;}",
      ".dillz-privacy-stats{width:100%;border-collapse:separate;border-spacing:12px;margin:0 0 28px;table-layout:fixed;}",
      ".dillz-privacy-stats td,.dillz-privacy-stat-card{background:#F7FAF1!important;border:1px solid #DDE8C8!important;border-radius:18px!important;padding:18px!important;text-align:center!important;color:#111111!important;}",
      ".dillz-privacy-stats strong,.dillz-privacy-stat-card div:first-child{display:block;color:#006d3d!important;font-size:12px!important;font-weight:800!important;letter-spacing:.08em!important;text-transform:uppercase!important;margin-bottom:6px!important;}",
      ".dillz-privacy-section-heading{font-size:22px!important;line-height:1.2!important;margin:30px 0 10px!important;color:#006d3d!important;font-weight:900!important;}",
      ".dillz-privacy-root h3{font-size:16px!important;line-height:1.3!important;margin:18px 0 8px!important;color:#111111!important;font-weight:900!important;}",
      ".dillz-privacy-data-table{width:100%;border-collapse:collapse;font-size:14px;line-height:1.6;margin:0 0 12px;}",
      ".dillz-privacy-data-table th{background:#F7FAF1;border:1px solid #DDE8C8;padding:12px;color:#006d3d;text-align:left;}",
      ".dillz-privacy-data-table td{border:1px solid #e6e6e6;padding:12px;vertical-align:top;}",
      ".dillz-privacy-contact-heading{background:#111111!important;color:#e0e31c!important;border-radius:22px 22px 0 0!important;padding:28px 28px 0!important;margin:28px 0 0!important;font-size:24px!important;line-height:1.2!important;}",
      ".dillz-privacy-contact-copy{background:#111111!important;color:#f2f2f2!important;border-radius:0 0 22px 22px!important;padding:10px 28px 28px!important;margin:0 0 18px!important;}",
      ".dillz-privacy-contact-copy a{color:#e0e31c!important;}",
      "@media screen and (max-width:740px){.dillz-privacy-root{padding:0 8px}.dillz-privacy-stats,.dillz-privacy-stats tbody,.dillz-privacy-stats tr,.dillz-privacy-stats td{display:block;width:100%;}.dillz-privacy-stats{border-spacing:0}.dillz-privacy-stats td{margin:0 0 12px}.dillz-privacy-stat-card{display:block!important;width:100%!important;margin:0 0 12px!important}.dillz-privacy-hero-title{font-size:28px!important}}"
    ].join("");

    document.head.appendChild(style);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", enhanceDillzPrivacyPolicy);
  } else {
    enhanceDillzPrivacyPolicy();
  }
})();

(function () {
  var dillzOpenedProductOptions = {};

  function setupDillzProductStickyForm() {
    var StickyForm = customElements.get("product-sticky-form");

    if (!StickyForm || StickyForm.prototype.dillzVisibilityPatched) {
      return;
    }

    StickyForm.prototype.dillzVisibilityPatched = true;
    StickyForm.prototype._setupVisibilityObservers = function () {
      var paymentContainer = document.getElementById("MainPaymentContainer");
      var paymentForm = paymentContainer ? paymentContainer.closest("form") : null;
      var footer = document.querySelector(".shopify-section--footer");
      var stickyHeaderOffset = typeof getStickyHeaderOffset === "function" ? getStickyHeaderOffset() : 0;
      var pocketMedia = window.matchMedia(window.themeVariables.breakpoints.pocket);
      var stickyFormId = this.getAttribute("form-id");

      if (!paymentForm) {
        return;
      }

      var refreshPaymentVisibility = function () {
        var paymentFormRect = paymentForm.getBoundingClientRect();
        var paymentButton = paymentForm.querySelector("[data-product-add-to-cart-button]");
        var paymentButtonRect = paymentButton ? paymentButton.getBoundingClientRect() : null;

        this._isPaymentContainerVisible = Boolean(
          paymentButtonRect &&
          paymentButtonRect.height > 0 &&
          paymentButtonRect.top >= stickyHeaderOffset &&
          paymentButtonRect.bottom <= window.innerHeight
        );
        this._isPaymentContainerPassed = paymentFormRect.bottom <= stickyHeaderOffset;
      }.bind(this);

      this._isFooterVisible = false;
      this._isPaymentContainerPassed = false;
      this._isPaymentContainerVisible = false;
      this._dillzOptionsOpened = Boolean(dillzOpenedProductOptions[stickyFormId]);

      var updateVisibility = function () {
        var formId = this.getAttribute("form-id");
        var variantPicker = formId ? document.querySelector("variant-picker[form-id='" + CSS.escape(formId) + "']") : null;
        var optionsTarget = this.dataset.optionsTarget;
        var header = document.querySelector(".shopify-section--header");
        var reviewSectionTargeted = window.location.hash === "#judgeme_product_reviews";
        var chooseOptions = pocketMedia.matches && this.dataset.hasOptions === "true" && variantPicker && !this._dillzOptionsOpened && !this._isPaymentContainerVisible && (!this._isPaymentContainerPassed || reviewSectionTargeted) && !this._isFooterVisible;
        var button = this.querySelector("[data-product-add-to-cart-button]");
        var chooseOptionsLink = this.querySelector("[data-dillz-choose-options-link]");

        if (variantPicker && optionsTarget) {
          variantPicker.id = optionsTarget;

          if (header) {
            variantPicker.style.scrollMarginTop = Math.ceil(header.getBoundingClientRect().height + 16) + "px";
          }
        }

        if (pocketMedia.matches) {
          this.hidden = this._isPaymentContainerVisible || this._isFooterVisible;
        } else {
          this.hidden = !this._isPaymentContainerPassed;
        }

        if (chooseOptionsLink) {
          chooseOptionsLink.hidden = !chooseOptions;
        }

        if (button) {
          button.hidden = Boolean(chooseOptions);

          if (!button.disabled) {
            button.textContent = button.dataset.buttonContent;
          }
        }
      }.bind(this);

      var chooseOptionsLink = this.querySelector("[data-dillz-choose-options-link]");

      if (chooseOptionsLink && !chooseOptionsLink.hasAttribute("data-dillz-options-bound")) {
        chooseOptionsLink.setAttribute("data-dillz-options-bound", "");
        chooseOptionsLink.addEventListener("click", function () {
          var targetSelector = chooseOptionsLink.getAttribute("href");
          var target = targetSelector && targetSelector.charAt(0) === "#" ? document.getElementById(targetSelector.slice(1)) : null;
          var formId = this.getAttribute("form-id");
          var variantPicker = formId ? document.querySelector("variant-picker[form-id='" + CSS.escape(formId) + "']") : null;
          var productMedia = target ? target.closest("product-media") : null;
          var visibleThumbnailItems = productMedia ? Array.prototype.some.call(productMedia.querySelectorAll(".product__thumbnail-item"), function (thumbnailItem) {
            var thumbnailRect = thumbnailItem.getBoundingClientRect();
            var thumbnailStyle = window.getComputedStyle(thumbnailItem);

            return thumbnailRect.width > 0 && thumbnailRect.height > 0 && thumbnailStyle.display !== "none" && thumbnailStyle.visibility !== "hidden";
          }) : false;

          if (target && variantPicker && !visibleThumbnailItems) {
            var targetRect = target.getBoundingClientRect();
            var variantPickerRect = variantPicker.getBoundingClientRect();
            var stickyFormHeight = this.getBoundingClientRect().height;
            var minimumTargetTop = stickyHeaderOffset + 12;
            var preferredTargetTop = stickyHeaderOffset + 256;
            var maximumTargetTop = window.innerHeight - stickyFormHeight - 12 - (variantPickerRect.bottom - targetRect.top);
            var targetTop = Math.max(minimumTargetTop, Math.min(preferredTargetTop, maximumTargetTop));

            target.style.scrollMarginTop = Math.ceil(targetTop) + "px";
          } else if (target) {
            target.style.removeProperty("scroll-margin-top");
          }

          this._dillzOptionsOpened = true;
          dillzOpenedProductOptions[stickyFormId] = true;
          updateVisibility();
        }.bind(this));
      }

      var refreshFromLayout = function () {
        refreshPaymentVisibility();
        updateVisibility();
      }.bind(this);

      this.dillzRefreshVisibility = refreshFromLayout;

      this.intersectionObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.target === footer) {
            this._isFooterVisible = entry.isIntersecting;
          }

          if (entry.target === paymentForm) {
            refreshPaymentVisibility();
          }
        }, this);

        updateVisibility();
      }.bind(this), { rootMargin: "-" + stickyHeaderOffset + "px 0px 0px 0px" });

      this.intersectionObserver.observe(paymentForm);

      if (footer) {
        this.intersectionObserver.observe(footer);
      }

      refreshFromLayout();
    };

    document.querySelectorAll("product-sticky-form").forEach(function (stickyForm) {
      if (stickyForm.intersectionObserver) {
        stickyForm.intersectionObserver.disconnect();
      }

      stickyForm._setupVisibilityObservers();
    });

    if (!window.dillzStickyVisibilityEventsBound) {
      var scheduleStickyVisibilityRefresh = function () {
        if (window.dillzStickyVisibilityFrame) {
          return;
        }

        window.dillzStickyVisibilityFrame = window.requestAnimationFrame(function () {
          window.dillzStickyVisibilityFrame = null;

          document.querySelectorAll("product-sticky-form").forEach(function (stickyForm) {
            if (typeof stickyForm.dillzRefreshVisibility === "function") {
              stickyForm.dillzRefreshVisibility();
            }
          });
        });
      };

      window.addEventListener("scroll", scheduleStickyVisibilityRefresh, { passive: true });
      window.addEventListener("resize", scheduleStickyVisibilityRefresh);
      window.dillzStickyVisibilityEventsBound = true;
    }

  }

  if (customElements.get("product-sticky-form")) {
    setupDillzProductStickyForm();
  } else {
    customElements.whenDefined("product-sticky-form").then(setupDillzProductStickyForm);
  }
})();
